/**
 * Error handling utilities for consistent error management throughout the application
 */
import { logger } from './logger';
import { useToastUIStore } from '../store/toastUIStore';
import * as Sentry from '@sentry/react';
import { ErrorType, getErrorType } from './queryErrorUtils';

/**
 * Extracts a readable message from any type of error
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    } else if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
  }
  return 'An unexpected error occurred';
}

/**
 * Logs an error with consistent formatting using the application logger
 * The logger will automatically forward errors to Sentry in production
 */
export function logError(
  context: string,
  error: unknown,
  extraContext?: Record<string, unknown>
): void {
  logger.error(`Error in ${context}:`, error, {
    ...extraContext,
    errorContext: context,
  });
}

/**
 * Options for error handling
 */
export interface ErrorHandlingOptions {
  /** Whether to show a notification with the error message */
  showNotification?: boolean;
  /** A callback to execute after handling the error */
  callback?: (errorMessage: string) => void;
  /** Custom message prefix to show in notifications */
  notificationPrefix?: string;
  /** Extra context to include with the error */
  extraContext?: Record<string, unknown>;
  /** Tag the error with specific properties for Sentry */
  tags?: Record<string, string>;
  /** Set the error severity level */
  level?: Sentry.SeverityLevel;
  /** Whether to show a retry button */
  showRetry?: boolean;
  /** Callback for retry button */
  onRetry?: () => void;
}

/**
 * Handles common error scenarios with consistent formatting and behavior
 *
 * @param context A description of where or when the error occurred
 * @param error The error that was caught
 * @param options Additional options for handling the error
 * @returns The extracted error message
 */
export function handleError(
  context: string,
  error: unknown,
  options?: ErrorHandlingOptions
): string {
  const errorMessage = getErrorMessage(error);
  const errorType = getErrorType(error);

  // Prepare context data with tags
  const extraContext = {
    ...options?.extraContext,
    tags: options?.tags,
    severity: options?.level || 'error',
  };

  // Log the error (which also reports to Sentry)
  logError(context, error, extraContext);

  if (options?.showNotification) {
    // Use toast UI store for notifications
    try {
      const { addToast } = useToastUIStore.getState();
      if (addToast) {
        const severity = getSeverityFromErrorType(errorType);

        addToast({
          type: severity,
          title: getTitleFromSeverity(severity),
          message: errorMessage,
          duration: 7000,
          showRetry: options.showRetry || false,
          onRetry: options.onRetry,
          autoClose: true,
        });
      }
    } catch (err) {
      // Fail silently if toast store isn't available yet
      console.error('Failed to show error toast:', err);
    }
  }

  if (options?.callback) {
    options.callback(errorMessage);
  }

  return errorMessage;
}

/**
 * Map error type to toast severity
 */
function getSeverityFromErrorType(errorType: ErrorType): 'error' | 'warning' | 'info' {
  switch (errorType) {
    case ErrorType.OFFLINE:
    case ErrorType.QUOTA_EXCEEDED:
    case ErrorType.NOT_FOUND:
    case ErrorType.CONFLICT:
    case ErrorType.VALIDATION:
      return 'warning';
    case ErrorType.AUTH_REQUIRED:
    case ErrorType.AUTH_EXPIRED:
      return 'info';
    default:
      return 'error';
  }
}

/**
 * Get title for toast notification based on severity
 */
function getTitleFromSeverity(severity: 'error' | 'warning' | 'info'): string {
  switch (severity) {
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Information';
    case 'error':
    default:
      return 'Error';
  }
}

/**
 * Setup global error handlers for unhandled errors and promise rejections
 */
export function setupGlobalErrorHandling(): void {
  window.addEventListener('error', event => {
    logError('Unhandled error', event.error, {
      type: 'unhandled_error',
      location: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Use toast UI store for notifications
    try {
      const { addToast } = useToastUIStore.getState();
      if (addToast) {
        addToast({
          type: 'error',
          title: 'Unexpected Error',
          message: getErrorMessage(event.error),
          duration: 7000,
        });
      }
    } catch (err) {
      // Fail silently if toast store isn't available yet
      console.error('Failed to show error toast:', err);
    }
  });

  window.addEventListener('unhandledrejection', event => {
    logError('Unhandled promise rejection', event.reason, {
      type: 'unhandled_rejection',
      location: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Use toast UI store for notifications
    try {
      const { addToast } = useToastUIStore.getState();
      if (addToast) {
        addToast({
          type: 'error',
          title: 'Operation Failed',
          message: getErrorMessage(event.reason),
          duration: 7000,
        });
      }
    } catch (err) {
      // Fail silently if toast store isn't available yet
      console.error('Failed to show error toast:', err);
    }
  });
}
